import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Pencil, Trash2, UserPlus, User, Users, Search, Check, X, Mail, Phone, MailCheck, Tag, GraduationCap, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';
import { BASE_URL } from '@/config/api';
import AddTeacherForm from './AddTeacherForm';
import EditTeacherForm from './EditTeacherForm';
import TeacherCategoryPricesModal from './TeacherCategoryPricesModal';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious, PaginationEllipsis } from '@/components/ui/pagination';

const TEACHERS_PER_PAGE = 10;

interface TeachersManagerProps {
  small?: boolean;
}

const TeachersManager = ({ showInvitationButton, onInviteClick, small = false, onTeachersLoad }: any) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const { user, token, refreshUserData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [teachers, setTeachers] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [showPricesDialog, setShowPricesDialog] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const isMobile = useIsMobile();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);

  useEffect(() => {
    fetchTeachers();
  }, [token]);

  const fetchTeachers = async () => {
    if (!token) return;

    setLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/teachers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTeachers(data.teachers);
        if (onTeachersLoad) onTeachersLoad((data.teachers || []).length);
      } else {
        console.error('Failed to fetch teachers');
        toast({
          title: 'Error',
          description: 'Failed to load teachers',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error fetching teachers:', error);
      toast({
        title: 'Error',
        description: 'An unexpected error occurred',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddTeacher = async (firstname, lastname, email, isInvite, bg_color_agenda) => {
    setIsActionLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/teachers/add`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ firstname, lastname, email, isInvite, bg_color_agenda })
      });
      if (response.ok) {
        await fetchTeachers();
        // Refresh user data so wizard flags (hasCreatedTeacher) update across UI
        try { await refreshUserData?.(); } catch {}
        setShowAddDialog(false);
        toast({
          title: 'Success',
          description: 'Teacher added successfully',
        });
      } else {
        const errorData = await response.json();
        toast({
          title: 'Error',
          description: errorData.message || 'Failed to add teacher',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error adding teacher:', error);
      toast({
        title: 'Error',
        description: 'An unexpected error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleEditTeacher = async (id, firstname, lastname, email, profile_picture, bg_color_agenda, isInvite) => {
    setIsActionLoading(true);
    try {
        const formData = new FormData();
        formData.append('firstname', firstname);
        formData.append('lastname', lastname);
        formData.append('email', email);

        if (profile_picture) {
            formData.append('profile_picture', profile_picture);
        }

        if (bg_color_agenda) {
            formData.append('bg_color_agenda', bg_color_agenda);
        }

        formData.append('isInvite', isInvite);

        const response = await fetch(`${BASE_URL}/api/teachers/update/${id}`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Accept': 'application/json'
            },
            body: formData
        });

        if (response.ok) {
            await fetchTeachers();
            setShowEditDialog(false);
            setSelectedTeacher(null);
            toast({
                title: 'Success',
                description: 'Teacher updated successfully',
            });
        } else {
            const errorData = await response.json();
            toast({
                title: 'Error',
                description: errorData.message || 'Failed to update teacher',
                variant: 'destructive',
            });
        }
    } catch (error) {
        console.error('Error updating teacher:', error);
        toast({
            title: 'Error',
            description: 'An unexpected error occurred',
            variant: 'destructive',
        });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleTeacherSettings = async (teacher_id, can_view_others, can_view_teacher_invoice, selectedTeacherIds) => {
    try {
      const response = await fetch(`${BASE_URL}/api/teachers/settings`, {
        method: 'POST',
        headers: {
             'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          teacher_id: teacher_id,
          school_id: user.school_id,
          can_view_others: can_view_others,
          can_view_teacher_invoice: can_view_teacher_invoice,
          selectedTeacherIds: selectedTeacherIds
        })
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Teacher settings updated successfully',
        });
      } else {
        toast({
          title: 'Error',
          description: 'Failed to update teacher settings',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error updating teacher settings:', error);
      toast({
        title: 'Error',
        description: 'An unexpected error occurred while updating settings',
        variant: 'destructive',
      });
    }
  };

  const sendInvite = async (teacherId) => {
    try {
      const response = await fetch(`${BASE_URL}/api/invite/teacher/${teacherId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Invitation sent successfully',
        });
      } else {
        const errorData = await response.json();
        toast({
          title: 'Error',
          description: errorData.message || 'Failed to send invitation',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error sending invitation:', error);
      toast({
        title: 'Error',
        description: 'An unexpected error occurred',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteTeacher = async () => {
    if (!selectedTeacher) return;
    setIsActionLoading(true);
    try {

      const response = await fetch(`${BASE_URL}/api/teachers/delete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id: selectedTeacher.id })
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Teacher deleted successfully',
        });
        await fetchTeachers();
        setShowDeleteAlert(false);
        setSelectedTeacher(null);
      } else {
        const errorData = await response.json();
        toast({
          title: 'Error',
          description: errorData.message || 'Failed to delete teacher',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error deleting teacher:', error);
      toast({
        title: 'Error',
        description: 'An unexpected error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  const filteredTeachers = teachers.filter(teacher =>
    `${teacher.firstname} ${teacher.lastname}`.toLowerCase().includes(search.toLowerCase()) ||
    teacher.email?.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filteredTeachers.length / TEACHERS_PER_PAGE);
  const paginatedTeachers = filteredTeachers.slice(
    (currentPage - 1) * TEACHERS_PER_PAGE,
    currentPage * TEACHERS_PER_PAGE
  );

  const renderPaginationItems = () => {
    const items = [];
    const maxVisiblePages = isMobile ? 3 : 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        items.push(
          <PaginationItem key={i}>
            <PaginationLink
              isActive={currentPage === i}
              onClick={() => setCurrentPage(i)}
            >
              {i}
            </PaginationLink>
          </PaginationItem>
        );
      }
    } else {
      items.push(
        <PaginationItem key={1}>
          <PaginationLink
            isActive={currentPage === 1}
            onClick={() => setCurrentPage(1)}
          >
            1
          </PaginationLink>
        </PaginationItem>
      );

      let startPage, endPage;

      if (isMobile) {
        if (currentPage > 2) {
          items.push(
            <PaginationItem key="ellipsis-1">
              <PaginationEllipsis />
            </PaginationItem>
          );
        }

        if (currentPage !== 1 && currentPage !== totalPages) {
          items.push(
            <PaginationItem key={currentPage}>
              <PaginationLink isActive={true}>
                {currentPage}
              </PaginationLink>
            </PaginationItem>
          );
        }

        if (currentPage < totalPages - 1) {
          items.push(
            <PaginationItem key="ellipsis-2">
              <PaginationEllipsis />
            </PaginationItem>
          );
        }
      } else {
        startPage = Math.max(2, currentPage - 1);
        endPage = Math.min(startPage + 2, totalPages - 1);

        if (endPage === totalPages - 1) {
          startPage = Math.max(2, endPage - 2);
        }

        if (startPage > 2) {
          items.push(
            <PaginationItem key="ellipsis-1">
              <PaginationEllipsis />
            </PaginationItem>
          );
        }

        for (let i = startPage; i <= endPage; i++) {
          items.push(
            <PaginationItem key={i}>
              <PaginationLink
                isActive={currentPage === i}
                onClick={() => setCurrentPage(i)}
              >
                {i}
              </PaginationLink>
            </PaginationItem>
          );
        }

        if (endPage < totalPages - 1) {
          items.push(
            <PaginationItem key="ellipsis-2">
              <PaginationEllipsis />
            </PaginationItem>
          );
        }
      }

      items.push(
        <PaginationItem key={totalPages}>
          <PaginationLink
            isActive={currentPage === totalPages}
            onClick={() => setCurrentPage(totalPages)}
          >
            {totalPages}
          </PaginationLink>
        </PaginationItem>
      );
    }

    return items;
  };

    const CardComponent = small ? React.Fragment : Card;
      const extraProps = small ? {} : { className: "mb-6" };

  return (
    <CardComponent {...extraProps}>
      {!small && (
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2">
            <GraduationCap className="h-5 w-5" />
            {t('teachers.title')}
          </CardTitle>
        </CardHeader>
      )}
    <CardContent className={small ? "p-0" : ""}>
    <div className="space-y-4">
      {loading ? (
        <div className="flex justify-center p-8">
             <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        </div>
      ) : (
        <div>
          <div className="space-y-2">
            <div className="flex justify-between items-center gap-2 mb-5">
              {isMobile ? (
                <div className="relative" style={{ width: isSearchOpen ? '100%' : 'auto', transition: 'width 0.3s ease' }}>
                  {isSearchOpen ? (
                    <div className="flex items-center">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="text"
                        placeholder={t('teachers.searchPlaceholder')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-8 w-48"
                      />
                      <Button
                        variant="ghost"
                        onClick={() => setIsSearchOpen(false)}
                        className="h-9 w-9 p-2"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={() => setIsSearchOpen(true)}
                      className="h-9 w-9 p-2"
                    >
                      <Search className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ) : (
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder={t('teachers.searchPlaceholder')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-8 w-48"
                  />
                </div>
              )}
              <div className="flex gap-2">
                {showInvitationButton && (
                  <Button variant="outline" onClick={onInviteClick} className="w-full sm:w-auto">
                    {isMobile ? <MailCheck className="h-4 w-4" /> : <>
                      <MailCheck className="mr-2 h-4 w-4" />
                      Invitations
                    </>}
                  </Button>
                )}
                <Button id="settings-teachers-add-btn" onClick={() => setShowAddDialog(true)} className="w-full sm:w-auto">
                  {isMobile ? <UserPlus className="h-4 w-4" /> : <>
                    <UserPlus className="mr-2 h-4 w-4" />
                    {t('teachers.addTeacher')}
                  </>}
                </Button>
              </div>
            </div>

            {paginatedTeachers.map((teacher) => (
              <div
                key={teacher.id}
                className="flex items-center justify-between p-2 rounded-md bg-secondary/50"
              >
                <div
                  className={`cursor-pointer p-0 h-auto text-foreground hover:text-primary ${!isMobile ? 'font-medium' : 'text-sm'}`}
                  onClick={() => {
                    setSelectedTeacher(teacher);
                    setShowEditDialog(true);
                  }}
                >
                  <span className="flex items-center">
                    {!isMobile && (
                      <div className="mr-2">
                        {teacher.bg_color_agenda ? (
                          <div 
                            className="w-4 h-4 rounded-full" 
                            style={{ backgroundColor: teacher.bg_color_agenda }}
                          />
                        ) : (
                          <div className="w-4 h-4 rounded-full border border-gray-300" />
                        )}
                      </div>
                    )}
                    {teacher.firstname} {teacher.lastname}
                  </span>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setSelectedTeacher(teacher);
                      setShowPricesDialog(true);
                    }}
                    className="h-8 w-8 text-muted-foreground hover:text-foreground"
                    title={t('teachers.customizePrices')}
                  >
                    <Tag className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setSelectedTeacher(teacher);
                      setShowEditDialog(true);
                    }}
                    className="h-8 w-8 text-muted-foreground hover:text-foreground"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setSelectedTeacher(teacher);
                      setShowDeleteAlert(true);
                    }}
                    className="h-8 w-8 text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            {filteredTeachers.length === 0 && !loading && (
              <div className="text-center p-4 text-muted-foreground">
                {search ? t('teachers.noSearchResults') : t('teachers.noTeachers')}
              </div>
            )}
          </div>
          {totalPages > 1 && (
            <Pagination className="mt-4">
              <PaginationContent className="px-1">
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                    className={currentPage === 1 ? "pointer-events-none opacity-50" : ""}
                  />
                </PaginationItem>

                {renderPaginationItems()}

                <PaginationItem>
                  <PaginationNext
                    onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                    className={currentPage === totalPages ? "pointer-events-none opacity-50" : ""}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </div>
      )}

      <Dialog open={showAddDialog} onOpenChange={(open) => {
        setShowAddDialog(open);
        if (!open) {
          window.dispatchEvent(new CustomEvent('wizard-add-dialog-closed', { detail: { type: 'teacher' } }));
        } else {
          window.dispatchEvent(new CustomEvent('wizard-add-dialog-opened', { detail: { type: 'teacher' } }));
        }
      }}>
        <DialogContent className={isMobile ? "p-0 w-full h-[100dvh] max-h-[100dvh]" : "max-w-[600px]"}>
          <DialogHeader className={isMobile ? "px-6 py-4 border-b mt-1" : "pb-5"}>
            <DialogTitle>
              <div className="flex items-center gap-2">
                {t('teachers.addTeacher')}
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className={!isMobile ? "" : "p-6"}>
            <AddTeacherForm
              onSubmit={handleAddTeacher}
              onCancel={() => {
                setShowAddDialog(false);
                window.dispatchEvent(new CustomEvent('wizard-add-dialog-closed', { detail: { type: 'teacher' } }));
              }}
              isSubmitting={isActionLoading}
            />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className={isMobile ? "w-full h-[100dvh] max-h-[100dvh] flex flex-col" : "max-w-[600px] max-h-[80vh] flex flex-col"}>
          <DialogHeader className={isMobile ? "px-6 py-4 border-b mt-0 bg-gray-50" : "pb-5"}>
            <DialogTitle>
              <div className="flex items-center gap-2">
                {t('teachers.editTeacher')}
              </div>
            </DialogTitle>
          </DialogHeader>
          {selectedTeacher && (
            <div className={isMobile ? 'p-3 flex-1 overflow-auto mb-24 p-5' : 'flex-1 overflow-auto'}>
              {/* Add a key prop to force remount when selectedTeacher.id changes */}
              <EditTeacherForm
                teacher={selectedTeacher}
                teachers={teachers}
                onSubmit={(firstname, lastname, email, profile_picture, bg_color_agenda, isInvite) =>
                  handleEditTeacher(selectedTeacher.id, firstname, lastname, email, profile_picture, bg_color_agenda, isInvite)
                }
                onCancel={() => setShowEditDialog(false)}
                sendInvite={sendInvite}
                onOpenPricesDialog={() => {
                  setShowEditDialog(false);
                  setShowPricesDialog(true);
                }}
                onSettingChange={handleTeacherSettings}
                isSubmitting={isActionLoading}
                key={selectedTeacher.id}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showPricesDialog} onOpenChange={setShowPricesDialog}>
        <DialogContent className={isMobile ? "p-0 w-full h-[100dvh] max-h-[100dvh]" : "max-w-[800px] h-[80vh] max-h-[80vh] overflow-y-auto"}>
          <DialogHeader className={isMobile ? "px-6 py-4 border-b" : "px-6 py-4 border-b"}>
            <DialogTitle>
              <div className="flex items-center gap-2">
                <Tag className="h-5 w-5 text-primary" />
                {t('teachers.customizePrices')}
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="p-4">
            {selectedTeacher && (
              <TeacherCategoryPricesModal
                teacher={selectedTeacher}
                onClose={() => setShowPricesDialog(false)}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDeleteAlert} onOpenChange={setShowDeleteAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('teachers.deleteTeacherConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('teachers.deleteTeacherConfirmDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <div className="flex justify-end gap-2 mt-3">
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteTeacher} disabled={isActionLoading} className="bg-destructive text-destructive-foreground">
              {isActionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('common.delete')}
            </AlertDialogAction>
            </div>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </CardContent>
    </CardComponent>
  );
};

export default TeachersManager;
